/** @type {readonly string[]} */
export const STORAGE_PATH_PREFIXES = [
  'sliders/',
  'galleries/',
  'news/',
  'events/',
  'uploads/',
  'seed/',
  'pages/',
  'homepage/',
  'story-cards/',
]

const LOCAL_STORAGE_URL = /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/storage\/.*)$/i

/** Origin for uploaded files (production API host). Empty = same-origin /storage via Vite proxy. */
export function getStorageOrigin() {
  const fromEnv = import.meta.env.VITE_STORAGE_URL?.replace(/\/$/, '')
  if (fromEnv) return fromEnv

  const api = import.meta.env.VITE_API_URL?.trim()
  if (!api || api.startsWith('/')) return ''

  try {
    return new URL(api).origin
  } catch {
    return ''
  }
}

export function getApiBaseUrl() {
  const api = import.meta.env.VITE_API_URL?.trim()
  if (!api) return '/api'
  return api.replace(/\/$/, '')
}

export function isStorageRelativePath(path) {
  if (!path || typeof path !== 'string') return false
  const normalized = path.replace(/^\/+/, '')
  return STORAGE_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix))
}

export function isUploadedStorageUrl(url) {
  if (!url || typeof url !== 'string') return false
  return url.includes('/storage/') || isStorageRelativePath(url)
}

export function isSliderMediaUrl(url) {
  if (!url || typeof url !== 'string') return false
  return /\/(?:api\/)?slider\//.test(url)
}

function absolutizeStoragePath(path) {
  const origin = getStorageOrigin()
  if (!origin || !path.startsWith('/storage/')) return path
  return `${origin}${path}`
}

function isHeroSliderFilename(filename) {
  return /^\d{3}\.(jpe?g|png|webp)$/i.test(filename)
}

function resolveSliderUrl(value) {
  const queryIndex = value.indexOf('?')
  const pathPart = queryIndex === -1 ? value : value.slice(0, queryIndex)
  const querySuffix = queryIndex === -1 ? '' : value.slice(queryIndex)

  if (pathPart.startsWith('/api/slider/')) {
    const apiBase = getApiBaseUrl()
    if (apiBase.startsWith('http')) {
      const origin = apiBase.replace(/\/api\/?$/, '')
      return `${origin}${pathPart}${querySuffix}`
    }
    return `${pathPart}${querySuffix}`
  }

  if (pathPart.startsWith('/slider/')) {
    const filename = pathPart.split('/').pop()
    if (filename && isHeroSliderFilename(filename)) {
      const apiBase = getApiBaseUrl()
      const url = apiBase.startsWith('http')
        ? `${apiBase.replace(/\/api\/?$/, '')}/api/slider/${filename}`
        : `/api/slider/${filename}`
      return `${url}${querySuffix}`
    }

    return `${pathPart}${querySuffix}`
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value)
      if (parsed.pathname.startsWith('/slider/')) {
        return resolveSliderUrl(`${parsed.pathname}${parsed.search}`)
      }
      if (parsed.pathname.startsWith('/api/slider/')) {
        return value
      }
      if (parsed.pathname.startsWith('/storage/')) {
        return `${parsed.pathname}${parsed.search}`
      }
    } catch {
      return null
    }
  }

  return null
}

/**
 * Normalize CMS media paths to browser-ready URLs.
 * - sliders/foo.png -> /storage/sliders/foo.png (or full URL on production)
 * - /slider/001.JPG -> /api/slider/001.JPG (served from Laravel, not static dist)
 */
export function resolveMediaUrl(image, fallback = null) {
  if (image == null || image === '') return fallback ?? null

  const value = String(image).trim()
  if (!value) return fallback ?? null

  if (value.startsWith('blob:') || value.startsWith('data:')) return value

  const sliderUrl = resolveSliderUrl(value)
  if (sliderUrl) return sliderUrl

  if (/^https?:\/\//i.test(value)) {
    const localMatch = value.match(LOCAL_STORAGE_URL)
    if (localMatch) {
      return absolutizeStoragePath(localMatch[1])
    }

    try {
      const parsed = new URL(value)
      if (parsed.pathname.startsWith('/storage/')) {
        return absolutizeStoragePath(parsed.pathname)
      }
    } catch {
      return value
    }

    return value
  }

  if (value.startsWith('/storage/')) {
    return absolutizeStoragePath(value)
  }

  if (value.startsWith('/')) return value

  if (isStorageRelativePath(value)) {
    return absolutizeStoragePath(`/storage/${value.replace(/^\/+/, '')}`)
  }

  return value
}

export function previewImageSrc(image, blobPreview = null) {
  if (blobPreview) return blobPreview
  return resolveMediaUrl(image, null)
}

export function stripStoragePrefix(url) {
  if (!url || typeof url !== 'string') return url
  if (url.startsWith('/storage/')) return url.slice('/storage/'.length)

  const match = url.match(/\/storage\/(.+)$/)
  if (match) return match[1]

  return url
}

export function mediaFallback(index = 0) {
  const fallbacks = ['/slider/001.JPG', '/slider/002.JPG', '/slider/003.JPG', '/slider/004.JPG']
  return fallbacks[index % fallbacks.length]
}
